import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { useBlog } from '../../context/BlogContext';
import AdminBlogSidebar from '../../components/adminblog/AdminBlogSidebar';
import ConfirmModal from '../../components/ConfirmModal';
import '../../styles/AdminBlog.css';

export default function AdminBlogPosts() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAdmin();
  const { posts, createPost, deletePost, exportPosts, importPosts, refreshPosts } = useBlog();
  const fileInputRef = useRef(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/adminblog');
  }, [isAuthenticated, loading, navigate]);

  // Al entrar autenticado, recarga la lista completa (borradores incluidos).
  useEffect(() => {
    if (isAuthenticated) refreshPosts();
  }, [isAuthenticated, refreshPosts]);

  const filteredPosts = useMemo(() => {
    let result = posts;
    if (filter === 'published') result = result.filter(p => p.status === 'published');
    if (filter === 'draft') result = result.filter(p => p.status === 'draft');
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p => p.title.toLowerCase().includes(q) || p.category.toLowerCase().includes(q));
    }
    return result;
  }, [posts, filter, search]);

  const handleCreate = async () => {
    try {
      const newPost = await createPost({ title: 'New Post' });
      navigate(`/adminblog/edit/${newPost.id}`);
    } catch (e) {
      alert('No se pudo crear el post. ¿Sesión de admin caducada?');
    }
  };

  const handleDelete = (id, title) => {
    setDeleteConfirm({ id, title });
  };

  const confirmDelete = () => {
    if (deleteConfirm) deletePost(deleteConfirm.id);
    setDeleteConfirm(null);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const result = await importPosts(ev.target.result);
      alert(result.message);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  if (loading) return <div className="adminblog-loading">Loading...</div>;

  return (
    <div className="adminblog-page">
      <header className="adminblog-header">
        <div className="adminblog-header-left">
          <div className="adminblog-header-title">
            <h1>Blog Admin</h1>
          </div>
        </div>
        <div className="adminblog-header-right">
          <button className="adminblog-action-btn" onClick={exportPosts} style={{ marginRight: 8 }}>Export JSON</button>
          <button className="adminblog-action-btn" onClick={() => fileInputRef.current?.click()} style={{ marginRight: 8 }}>Import JSON</button>
          <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />
          <button className="adminblog-create-btn" onClick={handleCreate}>+ New Post</button>
        </div>
      </header>

      <div className="adminblog-layout">
        <AdminBlogSidebar />
        <main className="adminblog-main">
          <div className="adminblog-content">
            <div className="adminblog-page-header">
              <h2>All Posts</h2>
            </div>

            <div className="adminblog-filters">
              <div className="adminblog-search">
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="adminblog-filter-buttons">
                {['all', 'published', 'draft'].map(f => (
                  <button
                    key={f}
                    className={`adminblog-filter-btn ${filter === f ? 'active' : ''}`}
                    onClick={() => setFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)} {f === 'all' ? `(${posts.length})` : `(${posts.filter(p => p.status === f).length})`}
                  </button>
                ))}
              </div>
            </div>

            {filteredPosts.length > 0 ? (
              <table className="adminblog-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map(post => (
                    <tr key={post.id}>
                      <td>
                        <div className="adminblog-post-title-cell">
                          {post.thumbnail ? (
                            <img src={post.thumbnail} alt="" className="adminblog-post-thumb" />
                          ) : (
                            <div className="adminblog-post-thumb-placeholder">No img</div>
                          )}
                          <span>{post.title}</span>
                        </div>
                      </td>
                      <td>{post.category || '—'}</td>
                      <td>
                        <span className={`adminblog-status-badge ${post.status}`}>
                          {post.status}
                        </span>
                      </td>
                      <td>{new Date(post.updatedAt).toLocaleDateString()}</td>
                      <td>
                        <div className="adminblog-actions">
                          <Link to={`/adminblog/edit/${post.id}`} className="adminblog-action-btn">Edit</Link>
                          <Link to={`/journal/${post.slug}`} target="_blank" rel="noopener noreferrer" className="adminblog-action-btn">Preview</Link>
                          <button className="adminblog-action-btn delete" onClick={() => handleDelete(post.id, post.title)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="adminblog-no-posts">
                <p>No posts found.</p>
              </div>
            )}
          </div>
        </main>
      </div>
      <ConfirmModal
        isOpen={!!deleteConfirm}
        message={deleteConfirm ? `Delete "${deleteConfirm.title}"?` : ''}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
